/**
 * Initial Settings Presets Data
 * 
 * Creates a default preset for each initial activity type.
 * Each preset contains geolocation-specific settings that can vary by activity.
 * 
 * activityTypeId corresponds to the auto-generated IDs from initialActivityTypesData:
 *   1 = Biking
 *   2 = Walking
 *   3 = Jogging
 *   4 = Hiking
 *   5 = Swimming
 *   6 = Skating
 *   7 = Rowing
 * 
 * When activityTypeId is null, the preset is "universal" (can be used with any activity)
 */

const initialSettingsPresetsData = [
  // Biking - higher accuracy, faster recording for speed tracking
  {
    presetName: 'Default Biking',
    activityTypeId: 1,
    isActive: false,
    isDefault: true,
    createdAt: Date.now(),
    // Geolocation settings
    enableHighAccuracy: true,
    maximumAge: 0,
    timeout: 5000,
    // Recording constraints - faster capture for cycling
    useMinSpeedThreshold: true,
    minSpeedThreshold: 2.0, // ~7 km/h minimum
    useRecordingTimeInterval: true,
    recordingTimeInterval: 1000, // 1 second intervals
    // Notifications
    showNotificationWhenNotMoving: true,
    // Keep-alive helpers
    screenWakeLockAvailability: true,
    useTouchScreenProtection: true
  },
  
  // Walking - moderate settings
  {
    presetName: 'Default Walking',
    activityTypeId: 2,
    isActive: true, // This is the initial active preset
    isDefault: true,
    createdAt: Date.now() + 1,
    enableHighAccuracy: false,
    maximumAge: 0,
    timeout: 5000,
    useMinSpeedThreshold: true,
    minSpeedThreshold: 0.9, // ~3.2 km/h
    useRecordingTimeInterval: true,
    recordingTimeInterval: 2000,
    showNotificationWhenNotMoving: false,
    screenWakeLockAvailability: true,
    useTouchScreenProtection: false
  },
  
  // Jogging - similar to walking but faster threshold
  {
    presetName: 'Default Jogging',
    activityTypeId: 3,
    isActive: false,
    isDefault: true,
    createdAt: Date.now() + 2,
    enableHighAccuracy: true,
    maximumAge: 0,
    timeout: 5000,
    useMinSpeedThreshold: true,
    minSpeedThreshold: 1.5, // ~5.4 km/h
    useRecordingTimeInterval: true,
    recordingTimeInterval: 1500,
    showNotificationWhenNotMoving: true,
    screenWakeLockAvailability: true,
    useTouchScreenProtection: true
  },
  
  // Hiking - battery-saving mode
  {
    presetName: 'Default Hiking',
    activityTypeId: 4,
    isActive: false,
    isDefault: true,
    createdAt: Date.now() + 3,
    enableHighAccuracy: false, // Save battery on long hikes
    maximumAge: 5000,
    timeout: 10000,
    useMinSpeedThreshold: true,
    minSpeedThreshold: 0.5, // Slower walking pace
    useRecordingTimeInterval: true,
    recordingTimeInterval: 3000, // Less frequent for battery
    showNotificationWhenNotMoving: false,
    screenWakeLockAvailability: true,
    useTouchScreenProtection: false
  },
  
  // Swimming - minimal settings (GPS often unreliable in water)
  {
    presetName: 'Default Swimming',
    activityTypeId: 5,
    isActive: false,
    isDefault: true,
    createdAt: Date.now() + 4,
    enableHighAccuracy: true, // Try to get best signal
    maximumAge: 0,
    timeout: 10000, // Longer timeout for weak signal
    useMinSpeedThreshold: false, // Speed unreliable in water
    minSpeedThreshold: 0.5,
    useRecordingTimeInterval: true,
    recordingTimeInterval: 5000, // Less frequent
    showNotificationWhenNotMoving: false,
    screenWakeLockAvailability: true,
    useTouchScreenProtection: true // Protect from water touches
  },
  
  // Skating - fast speed tracking
  {
    presetName: 'Default Skating',
    activityTypeId: 6,
    isActive: false,
    isDefault: true,
    createdAt: Date.now() + 5,
    enableHighAccuracy: true,
    maximumAge: 0,
    timeout: 5000,
    useMinSpeedThreshold: true,
    minSpeedThreshold: 2.5, // ~9 km/h
    useRecordingTimeInterval: true,
    recordingTimeInterval: 1000,
    showNotificationWhenNotMoving: true,
    screenWakeLockAvailability: true,
    useTouchScreenProtection: true
  },
  
  // Rowing - water activity with better GPS than swimming
  {
    presetName: 'Default Rowing',
    activityTypeId: 7,
    isActive: false,
    isDefault: true,
    createdAt: Date.now() + 6,
    enableHighAccuracy: true,
    maximumAge: 0,
    timeout: 5000,
    useMinSpeedThreshold: true,
    minSpeedThreshold: 1.0, // ~3.6 km/h
    useRecordingTimeInterval: true,
    recordingTimeInterval: 2000,
    showNotificationWhenNotMoving: false,
    screenWakeLockAvailability: true,
    useTouchScreenProtection: false
  }
];

export default initialSettingsPresetsData;
